import type { MetaFunction } from "react-router";

import { LegalDocument } from "@/legal/document";

export const meta: MetaFunction = () => [
  { title: `Privacy Policy · ${publicEnv.siteTitle}` },
];

// Describe the private Inbox boundary, billing, explicit publication state, X
// identity, and third-party embeds used by public collections.
//
export default function Privacy() {
  return (
    <LegalDocument
      description="How Bookmarked FYI handles curator accounts, subscriptions, private saved links, public collections, and visits to collection pages."
      title="Privacy Policy"
      updated="August 11, 2026"
    >
      <section>
        <h2>1. Scope and controller</h2>
        <p>
          This Policy describes how {publicEnv.siteLegalEntity}
          (&ldquo;Bookmarked,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;) handles personal information through Bookmarked
          FYI. It covers curator accounts, subscriptions and Billing, private
          Studio data, and public profile and collection pages. X and Stripe
          handle information under their own policies when you use their
          services.
        </p>
      </section>

      <section>
        <h2>2. Information we collect</h2>
        <h3>X identity and account information</h3>
        <p>
          When you sign in with X, we receive the stable X user ID and current
          screen name needed to create your Bookmarked account and handle claim.
          The server-side OAuth exchange validates the returned X access token
          and secret but does not expose them outside the OAuth module, store
          them in our database, or retain them in your session. We do not
          request or import your private X Bookmarks.
        </p>

        <h3>Private library and public collections</h3>
        <p>
          We store the public X post URLs and IDs you paste, fallback titles,
          curator notes, Inbox membership, profile name and biography,
          collection titles and descriptions, ordering, colors, publication
          state, and revision timestamps. Inbox items and private collections
          are returned only to the signed-in account. A collection becomes
          public only after you use the publication control.
        </p>

        <h3>Subscriptions and payments</h3>
        <p>
          We store your organization identifier, Stripe customer and
          subscription identifiers, selected price, subscription and invoice
          status, payment-method summary, billing event history, and related
          timestamps. Stripe collects and processes complete payment-card and
          billing details. We do not receive or store complete card numbers.
        </p>

        <h3>Sessions, security, and technical information</h3>
        <p>
          We use a signed, HTTP-only session cookie backed by a server-side
          session record. A short-lived encrypted cookie value protects the X
          request-token secret during the OAuth callback. We may process IP
          address, request time, route, response status, browser or device
          information, and security events in ordinary server logs and in-memory
          rate limits to operate and protect the Service.
        </p>

        <h3>Communications</h3>
        <p>
          If you contact us, we receive your contact details, message, and any
          information you choose to include. X sign-in does not provide us with
          your email address, so Bookmarked does not obtain an account email
          from X.
        </p>
      </section>

      <section>
        <h2>3. Publication and X embeds</h2>
        <p>
          Public profiles and collections can expose your X handle, profile
          text, collection descriptions, fallback titles, curator notes,
          arrangement, and the public posts you selected. Search engines and
          other people may index, share, or copy public pages. Your Inbox and
          unpublished collections are not included in public loaders.
        </p>
        <p>
          Public collection cards load X&apos;s official widget in the
          visitor&apos;s browser. That connection discloses information such as
          the visitor&apos;s IP address and browser details directly to X, and X
          may use its own storage or receive information according to its
          policy. We request X&apos;s do-not-track embed option, but we do not
          control X&apos;s processing. Studio does not load the X embed script.
        </p>
      </section>

      <section>
        <h2>4. Sources of information</h2>
        <p>We receive information:</p>
        <ul>
          <li>
            from you when you curate, publish, manage Billing, or contact us;
          </li>
          <li>
            from X when you authenticate and from public X URLs you submit;
          </li>
          <li>
            from Stripe about customers, subscriptions, invoices, payment
            methods, and payment status; and
          </li>
          <li>
            automatically from your browser and our infrastructure when you use
            the Service.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. How we use information</h2>
        <p>We use information to:</p>
        <ul>
          <li>authenticate curators and maintain current handle claims;</li>
          <li>save private libraries and display collections you publish;</li>
          <li>provide subscription access, payment controls, and Billing;</li>
          <li>respond to support and privacy requests;</li>
          <li>prevent fraud, abuse, and unauthorized access;</li>
          <li>diagnose failures and maintain service reliability; and</li>
          <li>comply with law and enforce our agreements.</li>
        </ul>
        <p>
          We do not sell personal information, share it for cross-context
          behavioral advertising, use private library content for targeted ads,
          or make decisions with legal or similarly significant effects based
          solely on automated processing.
        </p>
      </section>

      <section>
        <h2>6. Cookies and browser storage</h2>
        <p>
          Bookmarked uses essential cookies for sign-in, OAuth security, safe
          return paths, and subscription plan selection. A theme preference may
          be stored locally in your browser. You can clear browser storage or
          block cookies, but doing so can sign you out or prevent account
          features from working.
        </p>
        <p>
          Bookmarked does not currently load our audience analytics or session
          replay. X embeds are third-party content and may use separate browser
          technologies controlled by X.
        </p>
      </section>

      <section>
        <h2>7. How we disclose information</h2>
        <p>We may disclose information to:</p>
        <ul>
          <li>
            service providers for hosting, databases, security, payments, and
            technical operations;
          </li>
          <li>X for the sign-in and embed interactions you request;</li>
          <li>Stripe for subscription and payment processing;</li>
          <li>
            advisers or authorities when reasonably necessary to comply with
            law, protect rights and safety, or investigate abuse; and
          </li>
          <li>
            a successor in a merger, financing, reorganization, or sale, subject
            to appropriate confidentiality and notice.
          </li>
        </ul>
        <p>
          We also disclose information when you intentionally publish it on a
          public profile or collection.
        </p>
      </section>

      <section>
        <h2>8. Legal bases</h2>
        <p>
          Where data-protection law requires a legal basis, we process account,
          library, subscription, and billing information to perform our contract
          with you; security, reliability, support, and limited operational
          records for our legitimate interests; information needed to satisfy
          legal obligations; and information based on consent where we
          specifically request it. You may withdraw consent for future
          processing at any time, without affecting earlier processing.
        </p>
      </section>

      <section>
        <h2>9. Retention and deletion</h2>
        <p>
          We generally retain account and library information while your account
          remains open. Canceling a subscription does not automatically delete
          the account or its data. We retain billing, security, dispute, and
          backup records for as long as reasonably necessary for their purpose
          and legal obligations, then delete or de-identify them.
        </p>
        <p>
          To request account deletion, email us from information reasonably
          connected to the account. Deletion removes the Bookmarked account,
          private library, and public collections after verification and any
          legally required retention; it does not delete posts or copies
          controlled by X or others.
        </p>
      </section>

      <section>
        <h2>10. International processing</h2>
        <p>
          We and our providers may process information in the United States and
          other countries whose laws differ from those where you live. Where
          required, we use a recognized transfer mechanism or another lawful
          safeguard. Contact us to ask about safeguards applicable to your
          information.
        </p>
      </section>

      <section>
        <h2>11. Security</h2>
        <p>
          We use reasonable administrative, technical, and organizational
          measures designed to protect information. No transmission, storage, or
          authentication system is completely secure, so we cannot guarantee
          absolute security.
        </p>
      </section>

      <section>
        <h2>12. Your rights and choices</h2>
        <p>
          Depending on where you live, you may have rights to access, correct,
          delete, restrict, object to, or receive a portable copy of personal
          information, and to appeal a denied request. You may also complain to
          your local data-protection authority. You have the right to object to
          processing based on legitimate interests where applicable.
        </p>
        <p>
          Submit a request to{" "}
          <a href={`mailto:${publicEnv.siteSupportEmail}`}>
            {publicEnv.siteSupportEmail}
          </a>
          . We may verify your identity and authority before completing it. We
          will not discriminate against you for exercising a privacy right.
          Because we do not sell personal information or share it for
          cross-context behavioral advertising, there is no sale or targeted-ad
          opt-out to exercise for our own processing.
        </p>
      </section>

      <section>
        <h2>13. Children</h2>
        <p>
          Bookmarked is not directed to children under 13, and we do not
          knowingly collect their personal information. If you believe a child
          under 13 provided personal information, contact us so we can
          investigate and delete it where appropriate.
        </p>
      </section>

      <section>
        <h2>14. Changes and contact</h2>
        <p>
          We may update this Policy as the Service or law changes. We will post
          the new effective date and provide reasonable notice of material
          changes. Questions and privacy requests may be sent to{" "}
          <a href={`mailto:${publicEnv.siteSupportEmail}`}>
            {publicEnv.siteSupportEmail}
          </a>
          .
        </p>
      </section>
    </LegalDocument>
  );
}
